<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { storeToRefs } from "pinia"
import { useToast } from "primevue/usetoast"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useFetchWrapper } from "@core/composables"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"

defineOptions({
    name: "HomeBuildingAddTagsDialog",
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /** Affiche le dialogue (contrôlé par le parent selon `modal.kind`). */
    visible: boolean
}

// ================================
// PROPS
// ================================
defineProps<Props>()

// ================================
// COMPOSABLES
// ================================
const toast = useToast()
const homeWorkspace = useHomeWorkspaceStore()
const { modal, companyTags } = storeToRefs(homeWorkspace)
const authStore = useAuthStore()
const { $createBuildingTag, $addCompanyTag } = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Étiquettes entreprise choisies dans le multi-select (pas encore sur le bâtiment). */
const pickedFromCompany = ref<string[]>([])
/** Étiquettes libres saisies (hors liste entreprise). */
const manualTags = ref<string[]>([])
/** Champ de saisie pour une nouvelle étiquette. */
const manualInput = ref("")

// ================================
// COMPUTED
// ================================
/** Identifiant bâtiment depuis la modale workspace. */
const getBuildingId = computed(
    () => (modal.value.meta.buildingId as string) ?? ""
)

/** Étiquettes déjà posées sur le bâtiment à l’ouverture. */
const getCurrentBuildingTags = computed(
    () => (modal.value.meta.tagNames as string[] | undefined) ?? []
)

/** Nom du bâtiment (sous-titre optionnel). */
const getBuildingName = computed(
    () => (modal.value.meta.name as string | undefined) ?? ""
)

/** Options du multi-select : étiquettes entreprise absentes du bâtiment. */
const getCompanyTagOptions = computed(() => {
    const onBuilding = new Set(getCurrentBuildingTags.value)
    return companyTags.value.filter((t) => !onBuilding.has(t))
})

/** Indique si le bouton de validation peut être actif. */
const getCanSubmit = computed(() => {
    return pickedFromCompany.value.length + manualTags.value.length > 0
})

/** Saisie manuelle sans espaces de bord. */
const getTrimmedManualInput = computed(() => manualInput.value.trim())

/**
 * Indique si le libellé saisi est déjà utilisé (bâtiment, file d’attente ou entreprise).
 */
const getIsManualInputDuplicate = computed(() => {
    const raw = getTrimmedManualInput.value
    if (!raw) return false
    const lower = raw.toLowerCase()
    return (
        manualTags.value.some((t) => t.toLowerCase() === lower) ||
        pickedFromCompany.value.some((t) => t.toLowerCase() === lower) ||
        getCurrentBuildingTags.value.some((t) => t.toLowerCase() === lower) ||
        companyTags.value.some((t) => t.toLowerCase() === lower)
    )
})

/** Désactive « Ajouter » si champ vide ou doublon. */
const getIsManualAddDisabled = computed(() => {
    return !getTrimmedManualInput.value || getIsManualInputDuplicate.value
})

// ================================
// INTERACTIONS
// ================================
/**
 * Ferme le dialogue workspace.
 */
function onClose(): void {
    homeWorkspace.closeModal()
}

/**
 * Réinitialise le formulaire à l’ouverture et recharge les étiquettes entreprise.
 */
function onDialogShow(): void {
    pickedFromCompany.value = []
    manualTags.value = []
    manualInput.value = ""
    void homeWorkspace.loadCompanyTags()
}

/**
 * Ajoute la saisie manuelle à la liste des nouvelles étiquettes.
 */
function onAddManualTag(): void {
    if (getIsManualAddDisabled.value) return
    const raw = getTrimmedManualInput.value
    manualTags.value = [...manualTags.value, raw]
    manualInput.value = ""
}

/**
 * Retire une étiquette manuelle avant validation.
 */
function onRemoveManualTag(tag: string): void {
    manualTags.value = manualTags.value.filter((t) => t !== tag)
}

const { fetch: runSubmitTags, isFetching: getIsSubmittingTags } =
    useFetchWrapper(
        async () => {
            const buildingId = getBuildingId.value
            if (!buildingId) return

            const toAttach = [...pickedFromCompany.value, ...manualTags.value]
            if (!toAttach.length) return

            const brandNewForCompany = toAttach.filter(
                (t) => !companyTags.value.includes(t)
            )

            if (brandNewForCompany.length) {
                if (!authStore.companyId) {
                    throw new Error("Entreprise non chargée")
                }
                await $addCompanyTag(authStore.companyId, brandNewForCompany)
            }
            await $createBuildingTag(buildingId, toAttach)

            const merged = [
                ...new Set([...getCurrentBuildingTags.value, ...toAttach]),
            ]
            homeWorkspace.applyBuildingTagsUpdate(buildingId, merged)
            await homeWorkspace.loadCompanyTags()
            toast.add({
                group: "notifications",
                severity: "success",
                summary: "Étiquettes",
                detail: "Les étiquettes ont été mises à jour.",
                life: 4000,
            })
            onClose()
        },
        (err) => {
            const msg =
                err instanceof ServiceError
                    ? err.message
                    : (err?.message ?? "Mise à jour impossible")
            toast.add({
                group: "notifications",
                severity: "error",
                summary: "Étiquettes",
                detail: msg,
                life: 6000,
            })
        }
    )

/**
 * Valide l’ajout des étiquettes sélectionnées.
 */
function onConfirm(): void {
    void runSubmitTags()
}
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOGUE — AJOUT D’ÉTIQUETTES -->
    <!-- ================================ -->
    <BaseDialog
        :visible="visible"
        modal
        header="Ajouter des étiquettes"
        class="w-[min(100%,32rem)]"
        @update:visible="
            (v: boolean) => {
                if (!v) onClose()
            }
        "
        @show="onDialogShow"
    >
        <div
            class="dashboard-building-add-tags-dialog flex flex-col gap-4 py-1"
        >
            <p v-if="getBuildingName" class="m-0 text-sm text-surface-600">
                {{ getBuildingName }}
            </p>
            <p class="m-0 text-sm text-surface-600">
                Sélectionnez des étiquettes existantes ou saisissez-en de
                nouvelles pour ce bâtiment.
            </p>
            <div class="flex flex-col gap-2">
                <label class="text-sm text-surface-600" for="dash-add-tags-ms"
                    >Étiquettes de l’entreprise</label
                >
                <BaseMultiSelect
                    id="dash-add-tags-ms"
                    v-model="pickedFromCompany"
                    :options="getCompanyTagOptions"
                    placeholder="Rechercher / sélectionner"
                    display="chip"
                    filter
                    class="w-full"
                />
            </div>
            <div class="flex flex-col gap-2">
                <label class="text-sm text-surface-600" for="dash-add-tags-new"
                    >Nouvelle étiquette</label
                >
                <div class="flex flex-wrap items-center gap-2">
                    <BaseInputText
                        id="dash-add-tags-new"
                        v-model="manualInput"
                        class="min-w-0 flex-1"
                        placeholder="Libellé"
                        :invalid="getIsManualInputDuplicate"
                        :aria-invalid="getIsManualInputDuplicate"
                        :aria-describedby="
                            getIsManualInputDuplicate
                                ? 'dash-add-tags-new-error'
                                : undefined
                        "
                        @keyup.enter="onAddManualTag"
                    />
                    <BaseButton
                        type="button"
                        label="Ajouter"
                        severity="secondary"
                        :disabled="getIsManualAddDisabled"
                        @click="onAddManualTag"
                    />
                </div>
                <p
                    v-if="getIsManualInputDuplicate"
                    id="dash-add-tags-new-error"
                    class="m-0 text-sm text-red-600"
                    role="alert"
                >
                    Cette étiquette existe déjà.
                </p>
                <div v-if="manualTags.length" class="flex flex-wrap gap-1.5">
                    <BaseChip
                        v-for="(tag, idx) in manualTags"
                        :key="`m-${idx}-${tag}`"
                        :label="tag"
                        removable
                        @remove="onRemoveManualTag(tag)"
                    />
                </div>
            </div>
        </div>
        <template #footer>
            <BaseButton label="Annuler" severity="secondary" @click="onClose" />
            <BaseButton
                label="Valider"
                :loading="getIsSubmittingTags"
                :disabled="!getCanSubmit"
                @click="onConfirm"
            />
        </template>
    </BaseDialog>
</template>
